"use server";

import { type EnquiryState, emptyEnquiry } from "./enquiry-schema";

// Deliberately permissive: the goal is to catch typos, not to police the
// address grammar. Delivery failure is the real validator.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export async function submitEnquiry(
  _prev: EnquiryState,
  formData: FormData,
): Promise<EnquiryState> {
  const read = (key: string) => String(formData.get(key) ?? "").trim();

  const values = {
    name: read("name"),
    organisation: read("organisation"),
    email: read("email"),
    phone: read("phone"),
    intent: read("intent") || "consultation",
    message: read("message"),
  };

  // Honeypot — real people leave this hidden field empty. Report success so a
  // bot gets no signal about why nothing happened.
  if (read("company_website")) {
    return { status: "success", errors: {}, values: emptyEnquiry.values };
  }

  const errors: EnquiryState["errors"] = {};

  if (values.name.length < 2) errors.name = "Please enter your name.";
  if (!EMAIL.test(values.email))
    errors.email = "Please enter a valid email address.";
  if (values.organisation.length < 2)
    errors.organisation = "Please enter your organisation.";
  if (values.message.length < 20)
    errors.message =
      "Please give us a little more detail (20 characters or more).";

  if (Object.keys(errors).length > 0) {
    return {
      status: "error",
      message: "Please correct the highlighted fields.",
      errors,
      values,
    };
  }

  // ------------------------------------------------------------------
  // TODO(delivery): wire this to the firm's inbox or CRM before launch.
  // Nothing is transmitted yet — a submission is validated and acknowledged
  // but goes no further. Drop in an email provider (Resend, Postmark, SES) or
  // a CRM webhook here, and keep the credentials in server-only env vars.
  // ------------------------------------------------------------------
  console.info("[enquiry] received", {
    intent: values.intent,
    organisation: values.organisation,
  });

  return {
    status: "success",
    message:
      "Thank you — your enquiry has been received. A member of our team will respond shortly.",
    errors: {},
    values: emptyEnquiry.values,
  };
}
